<template>
  <div v-title="$route.meta.title">
    <div class="page-heading">
      <h3>{{ $route.meta.title }}</h3>
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>考试培训管理</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.meta.title }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <layout-panel>
      <template slot="header">搜索</template>
      <el-form
        ref="searchForm"
        :model="search"
        label-width="108px"
        label-position="right"
        size="medium"
        @submit.native.prevent="onSearch()">
        <div class="re-el-form__flex">
          <el-form-item label="教程标题" prop="title">
            <el-input
              v-model="search.title"
              clearable
              placeholder="请输入教程标题搜索" />
          </el-form-item>

          <el-form-item label="教程类型" prop="content_type">
            <el-select
              v-model="search.content_type"
              class="width-max"
              clearable
              placeholder="请选择教程类型">
              <el-option
                v-for="item in courseOptions.contentTypeMaps"
                :key="item.value"
                :label="item.label"
                :value="item.value" />
            </el-select>
          </el-form-item>

          <el-form-item label="用户类型" prop="target_usertype">
            <el-select
              v-model="search.target_usertype"
              class="width-max"
              clearable
              placeholder="请选择用户类型">
              <el-option
                v-for="item in courseOptions.targetUserMaps"
                :key="item.value"
                :label="item.label"
                :value="item.value" />
            </el-select>
          </el-form-item>

          <el-form-item label="状态" prop="status">
            <el-select
              v-model="search.status"
              class="width-max"
              clearable
              multiple
              placeholder="请选择状态">
              <el-option
                v-for="item in courseOptions.statusMaps"
                :key="item.value"
                :label="item.label"
                :value="item.value" />
            </el-select>
          </el-form-item>

          <el-form-item label="是否通用" prop="is_common">
            <el-select
              v-model="search.is_common"
              class="width-max"
              clearable
              placeholder="请选择是否通用">
              <el-option
                v-for="item in courseOptions.isCommonMaps"
                :key="item.value"
                :label="item.label"
                :value="item.value" />
            </el-select>
          </el-form-item>

          <el-form-item label="所属知识库" prop="training_knowledge_id">
            <remote-select
              v-model="search.training_knowledge_id"
              class="width-max"
              clearable
              multiple
              keyword-key="name"
              label-key="name"
              value-key="id"
              placeholder="请输入知识库名称搜索"
              :query-request="queryTrainingKnowledge"
              :find-request="getTrainingKnowledge" />
          </el-form-item>

          <el-form-item label="所属客户" prop="customerid">
            <remote-select
              v-model="search.customerid"
              class="width-max"
              clearable
              multiple
              id-key="number"
              label-key="name"
              value-key="id"
              placeholder="请输入客户关键字"
              :query-request="getCustomerTable"
              :find-request="getCustomerDetail" />
          </el-form-item>

          <el-form-item label="创建人" prop="creator_id">
            <remote-select
              v-model="search.creator_id"
              class="width-max"
              clearable
              multiple
              id-key="id"
              keyword-key="name"
              label-key="name"
              value-key="id"
              placeholder="请输入创建人"
              :query-request="getBasicPlatformUsersIndex"
              :find-request="getPlatformUsersDetail" />
          </el-form-item>

          <el-form-item label="创建时间" prop="created_at">
            <el-date-picker
              v-model="search.created_at"
              style="width: 100%;"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              align="right"
              :default-time="['00:00:00', '23:59:59']"
              value-format="yyyy-MM-dd HH:mm:ss" />
          </el-form-item>
        </div>

        <el-form-item>
          <el-button type="primary" icon="el-icon-search" native-type="submit">搜索</el-button>
          <el-button @click="resetSearch()">重置</el-button>
        </el-form-item>
      </el-form>
    </layout-panel>

    <layout-panel>
      <template slot="header">列表</template>
      <router-link to="/training/user-course/create">
        <el-button type="primary" size="small">创建教程</el-button>
      </router-link>
      <el-table
        :data="table"
        stripe
        highlight-current-row
        style="width: 100%">
        <el-table-column prop="id" label="ID" width="80" />

        <el-table-column prop="title" label="教程标题" min-width="200">
          <template slot-scope="scope">
            <span class="course-title">{{ scope.row.title }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="content_type" label="教程类型" width="110">
          <template slot-scope="scope">
            <span class="course-tag" :class="getTagClass('content', scope.row.content_type)">
              {{ getContentTypeLabel(scope.row.content_type) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column prop="target_usertype" label="用户类型" width="120">
          <template slot-scope="scope">
            <span class="course-tag" :class="getTagClass('user', scope.row.target_usertype)">
              {{ getTargetUserLabel(scope.row.target_usertype) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column prop="is_common" label="是否通用" width="100">
          <template slot-scope="scope">
            <span class="course-tag" :class="getTagClass('common', scope.row.is_common)">
              {{ getIsCommonLabel(scope.row.is_common) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column prop="knowledge_name" label="所属知识库" min-width="160">
          <template slot-scope="scope">
            <span class="knowledge-name">
              <i class="el-icon-folder knowledge-name__icon"></i>
              <span>{{ scope.row.knowledge_name }}</span>
            </span>
          </template>
        </el-table-column>

        <el-table-column prop="customer_name" label="所属客户" min-width="140">
          <template slot-scope="scope">
            <span>{{ scope.row.customer_name || '-' }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="created_at" label="创建时间" width="170" />

        <el-table-column prop="user_name" label="创建人" min-width="120">
          <template slot-scope="scope">
            <span>{{ scope.row.user_name || '-' }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="scope">
            <span class="course-tag" :class="getStatusTagClass(scope.row.status)">
              {{ getCourseStatusLabel(scope.row.status) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column fixed="right" label="操作" width="210">
          <template slot-scope="scope">
            <router-link :to="`/training/user-course/update/${scope.row.id}`">
              <el-button type="primary" size="mini" plain>编辑</el-button>
            </router-link>
            <el-button
              v-if="Number(scope.row.status) === consts.STATUS_NEW"
              type="success"
              size="mini"
              plain
              @click="handleCourseAction(scope.row, 'online')">上架</el-button>
            <el-button
              v-if="Number(scope.row.status) === consts.STATUS_ONLINE"
              type="warning"
              size="mini"
              plain
              @click="handleCourseAction(scope.row, 'offline')">下架</el-button>
            <el-button
              v-if="isSuperAdmin && Number(scope.row.status) === consts.STATUS_OFFLINE"
              type="danger"
              size="mini"
              plain
              @click="handleCourseAction(scope.row, 'ban')">作废</el-button>
            <el-button
              v-if="Number(scope.row.status) === consts.STATUS_OFFLINE"
              type="success"
              size="mini"
              plain
              @click="handleCourseAction(scope.row, 'online')">上架</el-button>
            <el-button
              v-if="isSuperAdmin && Number(scope.row.status) === consts.STATUS_BANNED"
              type="primary"
              size="mini"
              plain
              @click="handleCourseAction(scope.row, 'active')">激活</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination">
        <el-pagination
          background
          :current-page="pagination.current_page"
          :page-sizes="[20, 50, 100, 200]"
          :page-size="pagination.per_page"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          @size-change="handleSizePageChange"
          @current-change="handleCurrentPageChange" />
      </div>
    </layout-panel>
  </div>
</template>

<script>
import table from '@core/mixins/table'
import trainingMixins from '@views/training/training.mixins'
import { isSuperAdminUser } from '@views/training/training-user'
import {
  consts,
  getCourseStatusActions,
  getCourseStatusLabel,
  getCourseStatusType,
  getOptionLabel,
  opts,
} from '@views/training/training-course.constants'
import { NumberArray } from '@utils/common'

export default {
  mixins: [table, trainingMixins],
  data () {
    return {
      consts,
      courseOptions: opts,
      search: {
        title: null,
        content_type: null,
        target_usertype: null,
        status: [],
        is_common: null,
        training_knowledge_id: [],
        customerid: [],
        creator_id: [],
        created_at: [],
      },
      searchFieldType: {
        content_type: Number,
        target_usertype: Number,
        status: NumberArray,
        is_common: Number,
        training_knowledge_id: NumberArray,
        customerid: Array,
        creator_id: NumberArray,
        created_at: Array,
      },
      table: [],
      pagination: {
        current_page: 1,
        per_page: 50,
        total: 0,
      }
    }
  },
  computed: {
    ...Vuex.mapState({
      loginUser: state => state.login.user || {},
    }),
    isSuperAdmin () {
      return isSuperAdminUser(this.loginUser)
    },
  },
  methods: {
    ...request.map([
      'queryTrainingKnowledge',
      'getTrainingKnowledge',
      'getCustomerTable',
      'getCustomerDetail',
      'getBasicPlatformUsersIndex',
      'getPlatformUsersDetail',
    ]),
    getTableData (params) {
      return request.page('queryUserTrainingCourse', params)
    },
    resetSearch () {
      this.$refs[this.refSearchForm].resetFields()
      this.onSearch()
    },
    getContentTypeLabel (value) {
      return getOptionLabel(this.courseOptions.contentTypeMaps, value) || '-'
    },
    getTargetUserLabel (value) {
      return getOptionLabel(this.courseOptions.targetUserMaps, value) || '-'
    },
    getIsCommonLabel (value) {
      return getOptionLabel(this.courseOptions.isCommonMaps, value) || '-'
    },
    getCourseStatusLabel,
    getStatusTagClass (status) {
      return `course-tag--${getCourseStatusType(status)}`
    },
    getTagClass (type, value) {
      if (type === 'content') {
        if (Number(value) === this.consts.CONTENT_TYPE_IMAGE) {
          return 'course-tag--primary'
        }
        if (Number(value) === this.consts.CONTENT_TYPE_VIDEO) {
          return 'course-tag--success'
        }
        return 'course-tag--warning'
      }
      if (type === 'user') {
        return Number(value) === this.consts.TARGETUSER_TYPE_USER
          ? 'course-tag--primary'
          : 'course-tag--warning'
      }
      if (type === 'common') {
        return Number(value) === this.consts.IS_COMMON_SHARED
          ? 'course-tag--success'
          : 'course-tag--default'
      }
      return 'course-tag--default'
    },
    getActionConfig (status, name) {
      return getCourseStatusActions(status, this.isSuperAdmin).find(action => action.name === name)
    },
    handleCourseAction (row, name) {
      const action = this.getActionConfig(row.status, name)
      if (!action) {
        return
      }
      this.$confirm(`确定要${action.label}教程【${row.title}】吗？`, '提示', { type: 'warning' }).then(() => {
        request.common(action.apiName, row.id).then(() => {
          this.$message.success(action.successText)
          this.onRefresh()
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.course-title {
  display: inline-flex;
  align-items: center;
  line-height: 20px;
  color: #1f2d3d;
}

.knowledge-name {
  display: inline-flex;
  align-items: center;
  line-height: 20px;
  color: #1f2d3d;

  &__icon {
    margin-right: 8px;
    color: #f5a623;
    font-size: 14px;
  }
}

.course-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  height: 22px;
  padding: 0 12px;
  border-radius: 11px;
  box-sizing: border-box;
  font-size: 12px;
  line-height: 22px;
  white-space: nowrap;
}

.course-tag--primary {
  color: #409EFF;
  background-color: #ecf5ff;
}

.course-tag--success {
  color: #00a854;
  background-color: #d9f7e7;
}

.course-tag--warning {
  color: #e6a23c;
  background-color: #fdf6ec;
}

.course-tag--danger {
  color: #f56c6c;
  background-color: #fef0f0;
}

.course-tag--info,
.course-tag--default {
  color: #1f2d3d;
  background-color: #f4f5f7;
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/course/UserTrainingCourseTable.vue